import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import CreditLine, {
  CreditLineCanada,
  CreditLineUsa,
} from "@repo/kikoff-models/lending/CreditLine";
import Subscription, {
  SubscriptionCanada,
  SubscriptionUsa,
} from "@repo/kikoff-models/Subscription";
import t from "@repo/localization/string.macro";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import usRpc from "@repo/protobuf/api/usRpc";
import { ChangeUserSubscriptionAutorenewalRequest } from "@repo/protobuf/gen/kikoff_canada/protobuf/services/subscription_service_pb";
import { ListCatalogRequest } from "@repo/protobuf/gen/web/public/shopping_pb";
import {
  AutoRenew as UsaAutoRenewPb,
  SubscriptionPlan as UsSubscriptionPlanPb,
} from "@repo/protobuf/gen/web/public/subscription_pb";
import assert from "@repo/utils/assert";
import UArray from "@repo/utils/UArray";
import UFunction from "@repo/utils/UFunction";
import UProxy from "@repo/utils/UProxy";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { track } from "~/analytics/client";

import { useCreditLines } from "./creditLines";

const initialState = {
  byId: {} as Record<Subscription.Id, Subscription>,
  all: [] as Subscription.Id[],
  active: null as Subscription.Id | null,
  planById: {} as Record<Subscription.Plan.Id, Subscription.Plan>,
  defaultPlans: [] as Subscription.Plan.Id[],
  planByAgreementId: {} as Record<
    CreditLine.Agreement.Id,
    Subscription.Plan.Id
  >,
};

export const { Provider, useSubscriptions, Context } = FeatureStore.init(
  "Subscriptions",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [creditLines, setCreditLines] = useCreditLines();

    const accessors = {
      byId: attachAction(
        (
          id: Subscription.Id | undefined | null
        ): Subscription.Resolved | undefined => {
          if (!id) return;

          const subscription = store.byId[id];
          if (!subscription) return;

          return UProxy.merge(subscription, {
            get plan() {
              return accessors.plan(subscription.planId)!;
            },
          });
        },
        () => actions.fetch()
      ),
      active: () => accessors.byId(store.active),
      recent: () =>
        UArray.maxBy(accessors.all(), ({ id, endAt }) => [
          // Prioritize active subscription
          store.active === id,
          endAt,
        ]),
      all: attachAction(
        () => store.all.map((id): Subscription.Resolved => accessors.byId(id)!),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
      plan: (id: Subscription.Plan.Id | undefined) => id && store.planById[id],
      planByAgreementId: (id: CreditLine.Agreement.Id | undefined) =>
        id && store.planById[store.planByAgreementId[id]!],
      plansByTier: attachAction(
        (tier: Subscription.Tier) =>
          store.defaultPlans
            .map((plan) => store.planById[plan]!)
            .filter((plan) => plan.tier === tier),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
      eligiblePlans: attachAction(
        () => {
          const activeSubscription = store.byId[store.active!];
          return store.defaultPlans
            .map((plan) => store.planById[plan]!)
            .filter(
              (plan) =>
                plan.tier !==
                store.planById[activeSubscription?.planId ?? ""]?.tier
            );
        },
        () => {
          actions.fetchPlans();
          actions.fetch();
        },
        () => (res) => res.length > 0
      ),
      defaultPlans: attachAction(
        () => store.defaultPlans.map((plan) => store.planById[plan]!),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
    };

    const mutations = {
      one(subscription: Subscription) {
        setStore("byId", subscription.id, reconcile(subscription));
      },
      many(subscriptions: Subscription[]) {
        batch(() => {
          for (const subscription of subscriptions) {
            mutations.one(subscription);
          }
        });
      },
      updateAll(subscriptions: Subscription[]) {
        mutations.many(subscriptions);
        setStore(
          "all",
          subscriptions.map(({ id }) => id)
        );
      },

      onePlan(plan: Subscription.Plan) {
        setStore("planById", plan.id, reconcile(plan));
        if (plan.creditLineAgreement)
          setStore("planByAgreementId", plan.creditLineAgreement.id, plan.id);
      },
      manyPlans(plans: Subscription.Plan[]) {
        batch(() => {
          for (const plan of plans) {
            mutations.onePlan(plan);
          }
        });
      },
      updatePlans(plans: Subscription.Plan[]) {
        mutations.manyPlans(plans);
        setStore(
          "defaultPlans",
          plans.map(({ id }) => id)
        );
      },
    };

    const us = {
      tier: {
        byProtoEnum: {
          [UsSubscriptionPlanPb.BASIC]: "basic",
          [UsSubscriptionPlanPb.PREMIUM]: "premium",
          [UsSubscriptionPlanPb.ULTIMATE]: "ultimate",
        } satisfies Partial<Record<UsSubscriptionPlanPb, Subscription.Tier>>,
        name: {
          basic: "Kikoff Credit Service",
          premium: "Kikoff Credit Service Premium",
          ultimate: "Kikoff Credit Service Ultimate",
        } satisfies Record<Subscription.Tier, string>,
      },
      actions: {
        fetchPlans() {
          return Promise.all([
            usRpc.Subscription.listSubscriptionPlans({})
              .SUCCESS(UFunction.identity)
              .elseThrow(t`Failed to fetch subscription plans.`),
            usRpc.Shopping.listCatalog({
              context: ListCatalogRequest.ListCatalogueContext.SELECT_PLAN,
            })
              .SUCCESS(UFunction.identity)
              .elseThrow(t`Failed to fetch subscription plans.`),
          ]).then(([{ subscriptionPlans }, { products }]) => {
            mutations.updatePlans(
              subscriptionPlans
                .map((plan) =>
                  SubscriptionUsa.Plan.normalize(
                    plan,
                    products.find(
                      (product) =>
                        product.plan ===
                        UsSubscriptionPlanPb[
                          plan.name.toUpperCase() as keyof typeof UsSubscriptionPlanPb
                        ]
                    )!
                  )
                )
                // TODO: Standalone subscription
                .filter((plan) => plan.creditLineAgreement)
            );
          });
        },
        fetch() {
          return usRpc.Subscription.getUserSubscription({}).then(
            ({ userSubscription }) => {
              if (!userSubscription) return;

              batch(() => {
                // Prevent this from potentially overwriting plan normalized
                // with product which will have strictly more information
                if (!accessors.plan(userSubscription.planInfo!.id))
                  mutations.onePlan(
                    SubscriptionUsa.Plan.normalize(userSubscription.planInfo!)
                  );
                const normalized = SubscriptionUsa.normalize(userSubscription!);
                mutations.updateAll([normalized]);
                setStore("active", normalized.id);
              });
            }
          );
        },
        start: UFunction.todo,
        changePlan: UFunction.todo,
        // TODO: `openSubscriptionAndCreditLine` should be split, until then we
        // need to combine these steps in all api adapters.
        startWithCreditLine(planId: Subscription.Plan.Id) {
          const plan = accessors.plan(planId)!;
          const productToken = plan.metadata?.productToken;
          assert(
            productToken,
            "Subscription plan must be loaded with a product token, see SubscriptionUsa.Plan.normalize."
          );

          return usRpc.Subscription.openSubscriptionAndCreditLine({
            productToken,
          })
            .SUCCESS(({ creditLine, userSubscription }) => {
              batch(() => {
                setCreditLines.one(CreditLineUsa.normalize(creditLine!));
                {
                  // Don't update plan here, it's already in state and will not
                  // change and we also don't have the product to normalize with
                  // credit line agreement
                  const normalized = SubscriptionUsa.normalize(
                    userSubscription!
                  );
                  mutations.updateAll([normalized]);
                  setStore("active", normalized.id);
                }
              });
            })
            .elseThrow("Failed to open credit line.");
        },
        updateAutoRenew(autoRenew: Subscription.AutoRenew) {
          const subscription = accessors.recent()!;

          return usRpc.Subscription.updateAutoRenewalStatus({
            userSubscription: {
              token: subscription.id,
              autoRenew: {
                status: autoRenew.enabled
                  ? UsaAutoRenewPb.Status.ENABLED
                  : UsaAutoRenewPb.Status.DISABLED,
              },
            },
          })
            .SUCCESS(() => {
              actions.fetch();
            })
            .elseThrow("Failed to update subscription autorenew status.");
        },
      },
    };

    const canada = {
      actions: {
        fetchPlans() {
          return canadaRpc.Subscription.listSubscriptionPlans({}).then(
            ({ plans }) => {
              mutations.updatePlans(
                plans.map(SubscriptionCanada.Plan.normalize)
              );
            }
          );
        },
        fetch() {
          return canadaRpc.Subscription.listUserSubscriptions({}).then(
            ({ userSubscriptions }) => {
              batch(() => {
                mutations.manyPlans(
                  userSubscriptions.map(({ plan }) =>
                    SubscriptionCanada.Plan.normalize(plan!)
                  )
                );
                mutations.updateAll(
                  userSubscriptions.map(SubscriptionCanada.normalize)
                );

                setStore(
                  "active",
                  userSubscriptions.find((s) => s.active)?.id || null
                );
              });
            }
          );
        },
        start(planId: Subscription.Plan.Id) {
          return canadaRpc.Subscription.openSubscription({ planId }).then(
            () => {
              actions.fetch();
            }
          );
        },
        async startWithCreditLine(
          planId: Subscription.Plan.Id,
          options: {
            onStepStart?(step: "openCreditLine" | "startSubscription"): void;
          } = {}
        ) {
          const plan = accessors.plan(planId)!;

          const creditLineId =
            creditLines.activeCa()?.id ||
            (await (() => {
              options.onStepStart?.("openCreditLine");
              return setCreditLines.open(plan.creditLineAgreement!.id);
            })());

          options.onStepStart?.("startSubscription");
          return canadaRpc.Subscription.openSubscription({
            planId,
          }).then(({ userSubscription }) => {
            return batch(() => {
              mutations.onePlan(
                SubscriptionCanada.Plan.normalize(userSubscription!.plan!)
              );

              const normalized = SubscriptionCanada.normalize(
                userSubscription!
              );
              mutations.updateAll([normalized]);
              setStore("active", normalized.id);

              return { creditLineId, subscriptionId: normalized.id };
            });
          });
        },
        changePlan(planId: Subscription.Plan.Id) {
          const from = accessors.active()?.plan.tier!;
          const to = accessors.plan(planId)!.tier;

          track.subscription.changePlan("attempt", { from, to });

          return canadaRpc.Subscription.changeSubscriptionPlan({ planId })
            .then(({ creditLine }) => {
              track.subscription.changePlan("success", { from, to });

              actions.fetch();
              setCreditLines.one(CreditLineCanada.normalize(creditLine!));
            })
            .catch((e) => {
              track.subscription.changePlan("failure", {
                errors: e instanceof Error ? [e.message] : [],
                from,
                to,
              });
              throw e;
            });
        },
        updateAutoRenew(autoRenew: Subscription.AutoRenew) {
          return canadaRpc.Subscription.changeUserSubscriptionAutorenewal({
            userSubscriptionId: accessors.recent()!.id,
            state: autoRenew.enabled
              ? ChangeUserSubscriptionAutorenewalRequest.AutorenewState.ENABLED
              : ChangeUserSubscriptionAutorenewalRequest.AutorenewState
                  .DISABLED,
          }).then(({ userSubscription }) => {
            mutations.one(SubscriptionCanada.normalize(userSubscription!));
          });
        },
      },
    };

    const actions = withLogger(
      "action",
      import.meta.env.API_ADAPTER === "canada" ? canada.actions : us.actions
    );

    return { accessors, actions };
  }
);
